import type { CellContext, HeaderContext, Row } from "@tanstack/react-table";
import React, { type FC, useEffect, useMemo, useRef } from "react";
import { NonEntityColumn, type NonEntityColumnProps } from "../../NonEntityColumn";

import { IconButton, Stack, Tooltip } from "@mui/material";
import { ExpandIcon } from "@shared/frontend/icons/expand-icon/ExpandIcon";
import { NoExpandIcon } from "@shared/frontend/icons/no-expand-icon/NoExpandIcon";
import { useHyperTable } from "../../HyperTable";
import type { ActionButtonAfter } from "../hyper-table-actions-bar/ActionsBar";

export interface SingleRowAction<Entity> {
  text: string;
  icon: React.ReactNode;
  infoTooltip?: string;
  className?: string;
  onClick: (
    e: React.MouseEvent<HTMLElement>,
    row: Row<Entity>,
  ) =>
    | Pick<ActionButtonAfter, "refetch">
    | undefined
    | Promise<Pick<ActionButtonAfter, "refetch"> | undefined>
    | Promise<void>
    | void;
}

export type SingleRowActionGetter<Entity> = (
  selectedRow: Row<Entity>,
) => SingleRowAction<Entity>[];

type HyperTableExpandColumnProps<Entity> = Omit<Partial<NonEntityColumnProps<Entity>>, "valueConverter">

const DEFAULT_MIN_SIZE = 52;
const DEPTH_SLOT_PX = 32;

const HyperTableExpandColumn = <Entity extends any>(
  props: HyperTableExpandColumnProps<Entity>,
) => {
  const { ...override } = props;

  return (
    <NonEntityColumn<Entity>
      header={ExpandHeader}
      cell={ExpandCell}
      deps={[]}
      enableSorting={false}
      enablePinning={false}
      enableResizing={false}
      dontShowHeaderDivider
      preventRowClick
      defaultPinned
      cellClassName="expand"
      headerClassName="expand"
      size={DEFAULT_MIN_SIZE}
      minSize={DEFAULT_MIN_SIZE}
      {...override}
    />
  );
};

export default HyperTableExpandColumn;

type ExpandCellProps<T> = CellContext<T, unknown>

const ExpandCell = <T,>(props: ExpandCellProps<T>) => {
  const { row, column } = props;
  const { columnActions, table } = useHyperTable<T>();
  const isLastChild = row.getParentRow()?.subRows.at(-1) === row;
  const initialColumnMinSize = useRef<number>(
    column.columnDef.minSize ?? DEFAULT_MIN_SIZE,
  );

  const collectQualifiedParentDepths = (row: Row<T>): number[] => {
    const depths: number[] = [];
    let currentRow = row;

    while (currentRow?.getParentRow()) {
      const parentRow = currentRow.getParentRow();

      if (parentRow?.getIsExpanded()) {
        const lastChildOfParent = parentRow.subRows.at(-1);
        const isPartOfLastChildTree =
          lastChildOfParent?.id === currentRow.id ||
          lastChildOfParent?.subRows?.some((sub) => sub.id === currentRow.id);

        if (!isPartOfLastChildTree) {
          depths.push(parentRow.depth);
        }
      }

      currentRow = parentRow!;
    }

    return depths;
  };

  const openDepth = useMemo(() => collectQualifiedParentDepths(row), [row]);

  const renderPlaceholderLines = () => {
    return new Array(row.depth).fill(null).map((_, depthIndex) => {
      const isLastInDepth = depthIndex === row.depth - 1;

      return (
        <React.Fragment key={`${row.id}-depth-${depthIndex}`}>
          {!isLastInDepth ? (
            openDepth.includes(depthIndex) ? (
              <NoExpandIcon nestedChilde />
            ) : (
              <Stack width="1.8rem" padding="0 0.1rem" height="1rem" />
            )
          ) : (
            <NoExpandIcon isLastRow={isLastChild} />
          )}
        </React.Fragment>
      );
    });
  };

  const getMaxExpandedDepth = (): number => {
    return Math.max(...table.getExpandedRowModel().rows.map((r) => r.depth));
  };

  const maxDepth = useMemo(
    () => getMaxExpandedDepth(),
    [
      JSON.stringify(table.getRowModel().rows.map((r) => r.getIsExpanded())),
      table.getIsAllRowsExpanded(),
    ],
  );

  useEffect(() => {
    const nextSize = Math.max(
      DEFAULT_MIN_SIZE + maxDepth * DEPTH_SLOT_PX,
      initialColumnMinSize.current,
    );
    if (maxDepth !== column.columnDef.meta?.maxDepth) {
      columnActions.update(column.id, (prev) => ({
        ...prev,
        size: nextSize,
        minSize: nextSize,
        meta: {
          ...column.columnDef.meta,
          maxDepth,
        },
      }));
    }
  }, [maxDepth]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      gap={0.25}
      sx={{ width: "100%", minHeight: "2rem", px: 0.5, boxSizing: "border-box" }}
    >
      {row.depth > 0 && renderPlaceholderLines()}
      {row.getCanExpand() ? (
        <IconButton
          size="small"
          sx={{ padding: 0.25, width: 32, height: 32, minWidth: 32 }}
          onClick={() => row.toggleExpanded()}
          aria-label={row.getIsExpanded() ? "Collapse" : "Expand"}
        >
          <ExpandIcon expanded={row.getIsExpanded()} />
        </IconButton>
      ) : (
        row.depth === 0 && <Stack width="1.5rem" height="1.5rem" flexShrink={0} />
      )}
    </Stack>
  );
};

type ExpandHeaderProps<T> = HeaderContext<T, unknown>

const ExpandHeader: FC<ExpandHeaderProps<any>> = (props) => {
  return (
    <Stack
      width="100%"
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ px: 0.5, boxSizing: "border-box" }}
    >
      <Tooltip
        placement="bottom-start"
        title={props.table.getIsSomeRowsExpanded() ? "צמצם הכל" : "הרחב הכל"}
      >
        <IconButton
          onClick={() =>
            props.table.getIsSomeRowsExpanded()
              ? props.table.resetExpanded()
              : props.table.toggleAllRowsExpanded()
          }
          sx={{ padding: 0.25, width: 32, height: 32, minWidth: 32, mixBlendMode: "multiply" }}
        >
          <ExpandIcon header expanded={props.table.getIsSomeRowsExpanded()} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};
